export type FacebookVariableMappingEntry = {
  position: number
  source_type: 'lead' | 'facebook_field' | 'facebook_context' | 'constant'
  source_key: string
  fallback_text?: string
}

export type FacebookLeadgenQuestion = {
  key: string
  label: string
  type: string
}

export type MetaTemplateLike = {
  name: string
  language: string
  status?: string
  components?: Array<{ type?: string; text?: string }>
}

export type FacebookMappingSourceOption = {
  value: string
  label: string
  source_type: FacebookVariableMappingEntry['source_type']
  source_key: string
}

export const FACEBOOK_LEAD_SOURCE_OPTIONS: FacebookMappingSourceOption[] = [
  { value: 'lead:full_name', label: 'Nombre completo', source_type: 'lead', source_key: 'full_name' },
  { value: 'lead:first_name', label: 'Nombre', source_type: 'lead', source_key: 'first_name' },
  { value: 'lead:last_name', label: 'Apellidos', source_type: 'lead', source_key: 'last_name' },
  { value: 'lead:email', label: 'Email', source_type: 'lead', source_key: 'email' },
  { value: 'lead:phone', label: 'Teléfono', source_type: 'lead', source_key: 'phone' },
]

export const FACEBOOK_CONTEXT_SOURCE_OPTIONS: FacebookMappingSourceOption[] = [
  { value: 'facebook_context:form_id', label: 'ID formulario', source_type: 'facebook_context', source_key: 'form_id' },
  { value: 'facebook_context:form_name', label: 'Nombre formulario', source_type: 'facebook_context', source_key: 'form_name' },
  { value: 'facebook_context:campaign_id', label: 'ID campaña', source_type: 'facebook_context', source_key: 'campaign_id' },
  { value: 'facebook_context:campaign_name', label: 'Nombre campaña', source_type: 'facebook_context', source_key: 'campaign_name' },
]

export function countMetaTemplateBodyVariables(template: MetaTemplateLike): number {
  const body = template.components?.find((component) => component.type?.toUpperCase() === 'BODY')
  if (!body?.text) return 0
  const matches = body.text.match(/\{\{\d+\}\}/g)
  return matches ? matches.length : 0
}

export function buildFacebookFormSourceOptions(
  questions: FacebookLeadgenQuestion[] | undefined,
): FacebookMappingSourceOption[] {
  const formFields = (questions ?? []).map((question) => ({
    value: `facebook_field:${question.key}`,
    label: question.label || question.key,
    source_type: 'facebook_field' as const,
    source_key: question.key,
  }))
  return [...FACEBOOK_LEAD_SOURCE_OPTIONS, ...formFields, ...FACEBOOK_CONTEXT_SOURCE_OPTIONS]
}

export function buildCampaignSourceOptions(): FacebookMappingSourceOption[] {
  return [...FACEBOOK_LEAD_SOURCE_OPTIONS, ...FACEBOOK_CONTEXT_SOURCE_OPTIONS]
}

export function buildFacebookVariableMapping(
  count: number,
  previous: FacebookVariableMappingEntry[] = [],
): FacebookVariableMappingEntry[] {
  return Array.from({ length: count }, (_, index) => {
    const position = index + 1
    const existing = previous.find((item) => item.position === position)
    if (existing) {
      return { ...existing, position }
    }
    return { position, source_type: 'lead', source_key: '' }
  })
}

export function mappingEntryToSelectValue(entry: FacebookVariableMappingEntry): string {
  if (!entry.source_key) return ''
  return `${entry.source_type}:${entry.source_key}`
}

export function parseMappingSelectValue(value: string): Pick<FacebookVariableMappingEntry, 'source_type' | 'source_key'> {
  const [sourceType, ...rest] = value.split(':')
  return {
    source_type: sourceType as FacebookVariableMappingEntry['source_type'],
    source_key: rest.join(':'),
  }
}

export function normalizeFacebookVariableMapping(
  mapping: FacebookVariableMappingEntry[],
): FacebookVariableMappingEntry[] {
  return mapping
    .filter((entry) => entry.source_key)
    .map((entry) => ({
      position: entry.position,
      source_type: entry.source_type,
      source_key: entry.source_key,
      ...(entry.fallback_text ? { fallback_text: entry.fallback_text } : {}),
    }))
}

type ApiMappingEntry = Partial<FacebookVariableMappingEntry> & {
  field_key?: string
  variable_key?: string
}

export function normalizeFacebookVariableMappingFromApi(
  mapping: ApiMappingEntry[] | undefined,
): FacebookVariableMappingEntry[] {
  return (mapping ?? [])
    .map((entry) => {
      const position = Number(entry.position)
      if (!Number.isFinite(position) || position < 1) {
        return null
      }
      const sourceType = entry.source_type
      const sourceKey = entry.source_key || entry.field_key || entry.variable_key || ''
      if (!sourceType || !sourceKey) {
        return null
      }
      return {
        position,
        source_type: sourceType,
        source_key: sourceKey,
        ...(entry.fallback_text ? { fallback_text: entry.fallback_text } : {}),
      }
    })
    .filter((entry): entry is FacebookVariableMappingEntry => entry !== null)
}
